import { createRoute, lazyRouteComponent, stripSearchParams } from '@tanstack/react-router'
import type { StaffMemberRole, StaffSort } from '@volee/api-client'

import { requireBackoffice } from '../auth/guard.ts'
import { STAFF_ROLE_ORDER } from '../messages/index.ts'
import { parseOneOf, parsePage, parseQuery } from './params.ts'
import { rootRoute } from './root.tsx'

// The protected staff list. Search, filters, sort and page are typed search params (§4.4):
// a filtered view is a link, and the back button returns to the previous result.

// A string and not a boolean: the param has three states, and `active=false` and an
// absent `active` are different questions.
export const STAFF_STATES = ['in-service', 'revoked'] as const

export type StaffState = (typeof STAFF_STATES)[number]

export interface StaffSearch {
  /** 1-based: it is the number a person shares. */
  readonly page: number
  readonly q: string | null
  readonly role: StaffMemberRole | null
  readonly state: StaffState | null
  readonly sort: StaffSort
}

/** The orderings the API accepts. The default is the server's own. */
const SORTS: readonly StaffSort[] = ['email', '-email', 'active_from', '-active_from']

export const DEFAULT_SORT: StaffSort = 'email'

// `validateSearch` returns all five fields, so every `<Link to="/staff">` must declare
// them: this constant is what "back to the list" means.
export const DEFAULT_STAFF_SEARCH: StaffSearch = {
  page: 1,
  q: null,
  role: null,
  state: null,
  sort: DEFAULT_SORT,
}

export function parseRole(value: unknown): StaffMemberRole | null {
  return parseOneOf(STAFF_ROLE_ORDER, value)
}

export function parseState(value: unknown): StaffState | null {
  return parseOneOf(STAFF_STATES, value)
}

export function parseSort(value: unknown): StaffSort {
  return parseOneOf(SORTS, value) ?? DEFAULT_SORT
}

// `null` and not `undefined`: `buildUrl` drops both, and null is what "no filter" means
// everywhere else in `StaffFilters`.
export function activeFrom(state: StaffState | null): boolean | null {
  if (state === null) return null
  return state === 'in-service'
}

/** The list as a link target: path and the search params its type requires, in one place. */
export const staffListLink = { to: '/staff', search: DEFAULT_STAFF_SEARCH } as const

export const staffRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/staff',
  validateSearch: (search: Record<string, unknown>): StaffSearch => ({
    page: parsePage(search.page),
    q: parseQuery(search.q),
    role: parseRole(search.role),
    state: parseState(search.state),
    sort: parseSort(search.sort),
  }),
  search: {
    // Defaults leave the URL: a colleague receives /staff?role=admin, never
    // /staff?page=1&q=null&role=admin&state=null&sort=email.
    middlewares: [stripSearchParams(DEFAULT_STAFF_SEARCH)],
  },
  // Runs before the component is even fetched: without the role, no chunk downloads.
  beforeLoad: requireBackoffice,
  component: lazyRouteComponent(() => import('../pages/staff.tsx')),
})
